import {NgModule} from '@angular/core';
import {PreloadAllModules, RouterModule, Routes} from '@angular/router';
import {FirstAccessComponent} from './login/first-access/first-access.page';
import {LoginComponent} from './login/login.page';
import {AclGuard} from './shared/guards/acl.guard';
import {AuthGuard} from './shared/guards/auth.guard';
import {FeatureGuard} from './shared/guards/feature.guard';
import {PageTitleInterface} from './shared/interfaces/page-title.interface';
import {SettingsUsersSyncDetailsComponent} from './users/users-sync/sync-details/sync-details.component';
import {SettingsUsersSyncComponent} from './users/users-sync/users-sync.component';

const routes: Routes = [
    {
        path: '',
        redirectTo: '/login',
        pathMatch: 'full'
    },
    {
        path: 'login',
        component: LoginComponent,
        data: {title: PageTitleInterface.Login}
    },
    {
        path: 'first-access',
        component: FirstAccessComponent,
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.FirstAccess}
    },
    {
        path: 'usuarios/processo-de-sincronia',
        component: SettingsUsersSyncComponent,
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.UsersSyncProcess}
    },
    {
        path: 'usuarios/processo-de-sincronia/:syncId',
        component: SettingsUsersSyncDetailsComponent,
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.UserSyncProcess}
    },
    {
        path: 'users/visibility',
        loadChildren: () => import('./users/users-visibility/users-visibility.module').then(m => m.UsersVisibilityModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'dashboard',
        loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardPageModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.Dashboard}
    },
    {
        path: 'notepad',
        loadChildren: () => import('./notepad/notepad.module').then(m => m.NotepadModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.Notepad}
    },


    {
        path: 'free-evaluation-mid-year/:cycleId',
        loadChildren: () => import('./free-evaluation-mid-year/free-evaluation-mid-year.module').then(m => m.FreeEvaluationMidYearModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.FreeEvalMidYear}
    },


    {
        path: '360-evaluation/evaluation/:cycleId',
        loadChildren: () => import('./360-evaluation/evaluation/evaluation.module').then(m => m.Evaluation360Module),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.ThreeSixtyEval}
    },

    {
        path: '360-evaluation/selection/:cycleId',
        loadChildren: () => import('./360-evaluation/selection/evaluation-selection.module').then(m => m.Evaluation360SelectionModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.ThreeSixtySelection}
    },

    {
        path: '360-evaluation/validation/:cycleId',
        loadChildren: () => import('./360-evaluation/validation/evaluation-validation.module').then(m => m.Evaluation360ValidationModule),
        canActivate: [AuthGuard, AclGuard],
        data: {access: 'ManagerManagement', title: PageTitleInterface.ThreeSixtyValidation}
    },
    {
        path: 'free-evaluation-end-year/:cycleId',
        loadChildren: () => import('./free-evaluation-end-year/free-evaluation-end-year.module').then(m => m.FreeEvaluationEndYearModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.FreeEvalEndYear}
    },

    {
        path: 'self-evaluation-mid-year/:cycleId',
        loadChildren: () => import('./self-evaluation-mid-year/self-evaluation-mid-year.module').then(m => m.SelfEvaluationMidYearModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.SelfEvalMidYear}
    },

    {
        path: 'self-evaluation/:cycleId',
        loadChildren: () => import('./self-evaluation/self-evaluation.module').then(m => m.SelfEvaluationModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.SelfEval}
    },

    {
        path: 'manager-evaluation/:cycleId',
        loadChildren: () => import('./manager-evaluation/manager-evaluation.module').then(m => m.ManagerEvaluationModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.ManagerEval}
    },

    {
        path: 'head-evaluation/:cycleId',
        loadChildren: () => import('./head-evaluation/head-evaluation.module').then(m => m.HeadEvaluationModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.HeadEval}
    },

    {
        path: 'final-report/:cycleId/:userId',
        loadChildren: () => import('./final-report/final-report.module').then(m => m.FinalReportModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.UserFinalReport}
    },

    {
        path: 'final-feedback/:cycleId/:cycleProcessConfigId',
        loadChildren: () => import('./final-feedback/final-feedback.module').then(m => m.FinalFeedbackModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.FinalFeedback}
    },

    {
        path: 'final-feedback-validation/:cycleId/:cycleProcessConfigId',
        loadChildren: () => import('./final-feedback-validation/final-feedback-validation.module').then(m => m.FinalFeedbackValidationModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.FinalFeedbackValidation}
    },

    {
        path: 'generic-process/:cycleId/:cycleProcessConfigId',
        loadChildren: () => import('./generic-process/generic-process.module').then(m => m.GenericProcessModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.ManageGenericProcess}
    },

    {
        path: 'reports',
        loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.Reports}
    },

    {
        path: 'processes',
        loadChildren: () => import('./processes/processes.module').then(m => m.ProcessesPageModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.Process}
    },

    {
        path: 'manage-version/:processType/:versionId',
        loadChildren: () => import('./processes/manage-version/manage-version.module').then(m => m.ManageVersionModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.ManageProcess}
    },

    {
        path: 'performance-cycles',
        loadChildren: () => import('./performance-cycles/performance-cycles.module').then(m => m.PerformanceCyclesPageModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.PerformanceCycle}
    },

    {
        path: 'performance-cycles/:cycleId',
        loadChildren: () => import('./performance-cycles/configuration/configuration-performance-cycle.module').then(m => m.ConfigurationPerformanceCyclePageModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.ManageCycle}
    },

    {
        path: 'performance-cycles/:cycleId/cycle-participants',
        loadChildren: () => import('./performance-cycles/cycle-participants/cycle-participants.module').then(m => m.CycleParticipantsPageModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.ManageCycleParticipants}
    },

    {
        path: 'groups/:cycleId/:calibrationProcessId',
        loadChildren: () => import('./groups/groups.module').then(m => m.UserGroupsModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.CalibrationGroup}
    },

    {
        path: 'calibration/:cycleId/:calibrationProcessId/:groupId',
        loadChildren: () => import('./calibration/calibration.module').then(m => m.CalibrationModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.CalibrationUser}
    },

    {
        path: 'calibration/:cycleId/:groupId/report',
        loadChildren: () => import('./calibration/calibration-reports/calibration-reports.module').then(m => m.CalibrationReportsModule),
        data: {title: PageTitleInterface.CalibrationGroupReport}
    },
    {
        path: 'calibration/:cycleId/:groupId/:calibrationProcessId/report',
        loadChildren: () => import('./calibration/calibration-reports/calibration-reports.module').then(m => m.CalibrationReportsModule),
        data: {title: PageTitleInterface.CalibrationUserReport}
    },
    {
        path: 'calibration/:cycleId/:groupId/executive',
        loadChildren: () => import('./calibration/calibration-reports/calibration-reports.module').then(m => m.CalibrationReportsModule),
        data: {title: PageTitleInterface.CalibrationExecutiveBook}
    },
    {
        path: 'calibration/:cycleId/:groupId/excellent',
        loadChildren: () => import('./calibration/calibration-reports/calibration-reports.module').then(m => m.CalibrationReportsModule),
        data: {title: PageTitleInterface.CalibrationExcellentCard}
    },
    {
        path: 'ai-report/:cycleId/:groupId/report',
        ///home/filipe/projetos/performance/BTG-Performance/frontend/src/app/ai-standalone-modal-report/ai-standalone-modal-report.module.ts
        loadChildren: () => import('./ai-standalone-modal-report/ai-standalone-modal-report.module').then(m => m.AiStandaloneModalReportModule),
        data: {title: PageTitleInterface.AiManagementReport}
    },
    {
        path: 'users/admin',
        loadChildren: () => import('./users/users-admin/users-admin.module').then(m => m.UsersAdminPageModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'partner-config',
        loadChildren: () => import('./partner-config/partner-config.module').then(m => m.PartnerConfigModule),
        canActivate: [AuthGuard]
    },

    {
        path: 'okr',
        loadChildren: () => import('./okr/okr.module').then(m => m.OkrModule),
        canActivate: [AuthGuard]
    },

    {
        path: 'feedback',
        loadChildren: () => import('./feedback/feedback.module').then(m => m.FeedbackModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'external-evaluations',
        loadChildren: () => import('./external-evaluation/external-evaluations.module').then(m => m.ExternalEvaluationsModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.ExternalEvaluation}
    },
    {
        path: 'external-evaluation',
        loadChildren: () => import('./external-evaluation/manage-external-evaluation/manage-external-evaluation.module').then(m => m.ManageExternalEvaluationModule),
        data: {title: PageTitleInterface.ManageExternalEvaluation}
    },
    {
        path: 'hr-channel',
        loadChildren: () => import('./hr-channel/hr-channel.module').then(m => m.HrChannelModule),
        canActivate: [AuthGuard, FeatureGuard],
        data: {
            feature: 'hrChannel',
            title: PageTitleInterface.ManageHRChannel,
        }
    },
    {
        path: 'parameters',
        loadChildren: () => import('./parameters/parameters.module').then(m => m.ParametersModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.Parameters}
    },
    {
        path: 'profile',
        loadChildren: () => import('./profile/profile.module').then(m => m.ProfileModule),
        canActivate: [AuthGuard],
        data: {title: PageTitleInterface.Profile}
    },
    {
        path: '**',
        redirectTo: '/login'
    },

];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, { enableTracing: false, preloadingStrategy: PreloadAllModules })
    ],
    exports: [
        RouterModule
    ],
    providers: [
        AuthGuard,
        FeatureGuard
    ]
})
export class AppRoutingModule {
}
